import React, {
    ChangeEvent,
    KeyboardEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { Map } from 'immutable';

import MenuItem from 'flow/components/ui-components/MenuItem';
import TagRemovable from 'flow/components/custom-components/TagRemovable';
import ClickAwayListener from 'flow/components/ClickAwayListener';
import useAppContext from 'flow/hooks/useAppContext';
import { TicketKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import { sortStrings } from 'flow/utils/sorter-utils';
import Menu from 'flow/components/ui-components/Menu';
import { TagKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';

import {
    selectTagsTicketIsTaggedWith,
    selectAllTagsAvailableForTicketTagging,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserCanEditTags } from 'flow/selectors/ticket-permission-selectors';

import {
    addTagToTicket,
    removeTagFromTicket,
    fetchAllAvailableTags,
} from 'flow/actions/tickets-action';

import { TAG_STRINGS } from 'flow/utils/segment-utils';
import { segmentTrack } from 'flow/actions/segment-actions';
import useTicketAction from 'flow/hooks/useTicketAction';

//import TicketManageTagsPopoverOverview from 'flow/components/tickets/ticket-info-section/ticket-manage-tags-popup-overview';

interface Props {
    ticketKey: TicketKey;
}

interface Tag {
    key: TagKey;
    label: string;
}

const TicketTagsList = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();

    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const [inputValue, setInputValue] = useState('');
    const [inputAndMenuAreActive, setInputAndMenuAreActive] = useState(false);
    //const [manageTagsIsOpen, setManageTagsIsOpen] = useState(false);
    const [isFetchingTags, setIsFetchingTags] = useState(false);
    const [isPerformingAddTagRequest, setIsPerformingAddTagRequest] =
        useState(false);
    const [keyOfTagBeingRemoved, setKeyOfTagBeingRemoved] = useState(undefined);
    const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>(
        'bottom',
    );

    const allTags = useAppSelector(selectAllTagsAvailableForTicketTagging);
    const tagsTicketIsTaggedWith = useAppSelector((state) =>
        selectTagsTicketIsTaggedWith(state, ticketKey),
    );
    const userHasPermissionToAddRemoveCreateNewTags = useAppSelector((state) =>
        selectOwnUserCanEditTags(state, ticketKey),
    );

    useEffect(() => {
        setIsFetchingTags(true);
        dispatch(fetchAllAvailableTags()).finally(() => {
            setIsFetchingTags(false);
        });
    }, [dispatch, setIsFetchingTags]);

    const tagsAvailableForTagging = useMemo(() => {
        if (!tagsTicketIsTaggedWith || !allTags) return Map();

        return allTags
            .filter((tag: TypedMap<Tag>) => {
                const tagKey = tag.get('key');
                const tagIsAlreadyUsed = tagsTicketIsTaggedWith.get(tagKey);
                return !tagIsAlreadyUsed;
            })
            .sortBy(
                (tagsData: TypedMap<Tag>) => tagsData.get('label'),
                sortStrings,
            );
    }, [allTags, tagsTicketIsTaggedWith]);

    const tagsToDisplayInSelectableTagsList = useMemo(() => {
        if (!inputValue || !tagsAvailableForTagging)
            return tagsAvailableForTagging || Map();

        const inputValueLowerCase = inputValue.toLowerCase();
        // We don't want to exclude tags from the search if only case differs, i.e. if there's a tag
        // named 'Product' (with capital P) it should appear if searching for product (with lower case p)

        return tagsAvailableForTagging.filter((tag: TypedMap<Tag>) =>
            tag.get('label').toLowerCase().startsWith(inputValueLowerCase),
        );
    }, [inputValue, tagsAvailableForTagging]);

    const inputValueMatchesTagAlreadyUsed = useMemo(() => {
        if (!inputValue) return false;
        return !!tagsTicketIsTaggedWith.find(
            (tag: TypedMap<Tag>) => tag.get('label') === inputValue.trim(),
        );
    }, [inputValue, tagsTicketIsTaggedWith]);

    const inputValueMatchesTagAvailableForTagging = useMemo(() => {
        if (!inputValue) return false;
        return !!tagsToDisplayInSelectableTagsList.find(
            (tag: TypedMap<Tag>) => tag.get('label') === inputValue.trim(),
        );
    }, [inputValue, tagsToDisplayInSelectableTagsList]);

    const newTagMenuItemRef = useRef<HTMLDivElement>(null);
    const textInputRef = useRef<HTMLInputElement>(null);
    const firstMenuItemRef = useRef<HTMLDivElement>(null);
    const lastMenuItemRef = useRef<HTMLDivElement>(null);

    const onCloseMenuClicked = () => {
        setInputAndMenuAreActive(false);
        textInputRef.current.blur();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case 'Enter':
                setInputValue('');
                handleTextFieldClick();
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (lastMenuItemRef.current) {
                    lastMenuItemRef.current.focus();
                } else if (newTagMenuItemRef?.current) {
                    newTagMenuItemRef.current.focus();
                }

                break;
            case 'ArrowDown':
                event.preventDefault();
                if (firstMenuItemRef.current) {
                    firstMenuItemRef.current.focus();
                } else if (newTagMenuItemRef?.current) {
                    newTagMenuItemRef.current.focus();
                }

                break;
            case 'Backspace':
                textInputRef.current.focus();
                if (inputValue === '' && tagsTicketIsTaggedWith.size > 0) {
                    const lastTag = Array.from(
                        tagsTicketIsTaggedWith,
                        ([key, label]) => ({ key, label }),
                    ).pop();
                    handleRemoveTag(undefined, lastTag.key, 'key_backspace');
                }
                break;
            case 'Escape':
                onCloseMenuClicked();
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const inputElement = textInputRef.current;
        if (inputElement && inputAndMenuAreActive) {
            const inputRect = inputElement.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - inputRect.bottom;
            const spaceAbove = inputRect.top;

            if (spaceBelow < 200 && spaceAbove > spaceBelow) {
                setMenuPosition('top');
            } else {
                setMenuPosition('bottom');
            }
        }
    }, [inputAndMenuAreActive, textInputRef]);

    const handleTextFieldClick = () => {
        if (!userHasPermissionToAddRemoveCreateNewTags) return;

        setInputAndMenuAreActive(true);

        if (textInputRef.current) {
            textInputRef.current.focus();
        }
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const onClickAddExistingTag = (tagKey: string) => {
        setIsPerformingAddTagRequest(true);
        dispatch(
            segmentTrack(TAG_STRINGS.ADD_EXISTING_TAG, {
                component: 'Ticket panel',
            }),
        );

        runTicketAction(addTagToTicket(ticketKey, undefined, tagKey)).finally(
            () => {
                setIsPerformingAddTagRequest(false);
            },
        );

        setInputValue('');
        handleTextFieldClick();
    };

    const onClickCreateAddNewTag = () => {
        const tagText = inputValue.trim();
        if (!tagText) return;

        const existingTag = allTags.find(
            (tag: TypedMap<Tag>) => tag.get('label') === tagText,
        );
        if (existingTag) return;

        setIsPerformingAddTagRequest(true);

        dispatch(
            segmentTrack(TAG_STRINGS.CREATE_ADD_NEW_TAG, {
                component: 'Ticket panel',
            }),
        );
        runTicketAction(addTagToTicket(ticketKey, tagText)).finally(() => {
            setIsPerformingAddTagRequest(false);
        });

        setInputValue('');
        handleTextFieldClick();
    };

    const onClickRemoveTag = (event: any, tagKey: string) => {
        handleRemoveTag(event, tagKey, 'click');
    };

    const handleRemoveTag = (
        event: any,
        tagKey: string,
        interactionType: 'click' | 'key_backspace',
    ) => {
        if (event) event.stopPropagation();

        dispatch(
            segmentTrack(TAG_STRINGS.REMOVE, {
                component: 'Ticket panel',
                interactionType: interactionType,
            }),
        );

        setKeyOfTagBeingRemoved(tagKey);
        runTicketAction(removeTagFromTicket(ticketKey, tagKey)).finally(() => {
            setKeyOfTagBeingRemoved(undefined);
        });
    };

    /*
    const onCloseManageTagsPopover = () => {
        setManageTagsIsOpen(false);
    };

    const onManageTagsMenuClick = () => {
        if (!userHasPermissionToManageTags) return;
        setManageTagsIsOpen(true);
    };
    */

    const returnTagAlreadyInUseItemIfAppropriate = () => {
        if (!inputValue || !inputValueMatchesTagAlreadyUsed) return null;

        return (
            <div className="text-2xs py-2 ps-2 font-bold text-gray-400">
                {i18next.t('tags.already_applied')}
            </div>
        );
    };

    const returnCreateAndAddNewTagItemIfAppropriate = () => {
        if (
            !inputValue ||
            inputValueMatchesTagAlreadyUsed ||
            inputValueMatchesTagAvailableForTagging
        ) {
            return null;
        }

        return (
            <MenuItem
                className="text-2xs font-bold text-gray-500"
                onClick={onClickCreateAddNewTag}
                refObject={newTagMenuItemRef}
            >
                {i18next.t('tags.new', {
                    inputText: inputValue,
                })}
            </MenuItem>
        );
    };

    const returnHelperItemIfAppropriate = () => {
        if (inputValue || isFetchingTags) return null;

        if (allTags.isEmpty()) {
            return (
                <MenuItem
                    className="text-2xs font-bold text-gray-400"
                    disabled={true}
                >
                    {i18next.t('tags.no_created_type_new')}
                </MenuItem>
            );
        }

        if (tagsToDisplayInSelectableTagsList.isEmpty()) {
            return (
                <MenuItem
                    className="text-2xs font-bold text-gray-400"
                    disabled={true}
                >
                    {i18next.t('tags.type_create_new')}
                </MenuItem>
            );
        }

        return null;
    };

    return (
        <div className="relative items-center">
            <div className="mb-2 text-xs font-bold text-black">
                {i18next.t('tags.tags')}
            </div>
            <div
                className={twMerge(
                    'flex min-h-9 flex-wrap items-center rounded-sm border border-solid bg-gray-200 ps-2',
                    inputAndMenuAreActive
                        ? 'border-gray-300'
                        : 'border-transparent',
                )}
                onClick={handleTextFieldClick}
            >
                <div className="mt-1 mb-1 flex flex-wrap gap-1">
                    {tagsTicketIsTaggedWith &&
                        tagsTicketIsTaggedWith
                            .map((tag: TypedMap<Tag>) => (
                                <TagRemovable
                                    tagKey={tag.get('key')}
                                    text={tag.get('label')}
                                    onClickRemove={onClickRemoveTag}
                                    key={tag.get('key')}
                                    shouldDisplayRemovalButton={
                                        userHasPermissionToAddRemoveCreateNewTags
                                    }
                                    disabled={
                                        tag.get('key') ===
                                            keyOfTagBeingRemoved ||
                                        isPerformingAddTagRequest
                                    }
                                />
                            ))
                            .valueSeq()}
                </div>
                {userHasPermissionToAddRemoveCreateNewTags ? (
                    <input
                        ref={textInputRef}
                        aria-label={i18next.t('tags.click_to_add')}
                        className="flex h-6 w-full justify-center bg-gray-200 text-xs focus:outline-hidden"
                        placeholder={
                            inputAndMenuAreActive ||
                            !userHasPermissionToAddRemoveCreateNewTags
                                ? ''
                                : i18next.t('tags.click_to_add')
                        }
                        value={inputValue}
                        onChange={onInputChange}
                        onClick={handleTextFieldClick}
                        onKeyDown={inputAndMenuAreActive ? onKeyDown : null}
                        onFocus={handleTextFieldClick}
                    />
                ) : null}
            </div>
            {inputAndMenuAreActive ? (
                <ClickAwayListener
                    onClickAway={() => setInputAndMenuAreActive(false)}
                >
                    <Menu
                        className={twMerge(
                            'absolute start-0 max-h-40 w-[328px] overflow-auto text-xs font-bold',
                            menuPosition === 'bottom'
                                ? 'top-full'
                                : 'bottom-full -mb-5',
                        )}
                    >
                        {returnTagAlreadyInUseItemIfAppropriate()}
                        {returnCreateAndAddNewTagItemIfAppropriate()}
                        {allTags.isEmpty() ||
                        tagsToDisplayInSelectableTagsList.isEmpty()
                            ? returnHelperItemIfAppropriate()
                            : tagsToDisplayInSelectableTagsList
                                  .map((tag: TypedMap<Tag>) => {
                                      const index = Array.from(
                                          tagsToDisplayInSelectableTagsList.keys(),
                                      ).indexOf(tag.get('key'));
                                      return (
                                          <MenuItem
                                              key={tag.get('key')}
                                              className={twMerge(
                                                  'text-2xs font-bold focus:outline-hidden',
                                                  isPerformingAddTagRequest
                                                      ? 'text-gray-400'
                                                      : 'text-gray-500',
                                              )}
                                              disabled={
                                                  isPerformingAddTagRequest
                                              }
                                              onClick={() =>
                                                  onClickAddExistingTag(
                                                      tag.get('key'),
                                                  )
                                              }
                                              refObject={
                                                  index === 0
                                                      ? firstMenuItemRef
                                                      : index ===
                                                          tagsToDisplayInSelectableTagsList.size -
                                                              1
                                                        ? lastMenuItemRef
                                                        : null
                                              }
                                          >
                                              {tag.get('label')}
                                          </MenuItem>
                                      );
                                  })
                                  .valueSeq()}
                        {/*userHasPermissionToManageTags && !allTags.isEmpty() ? (
                            <>
                                <Divider className="me-2 ms-2" />
                                <MenuItem
                                    className="text-2xs font-bold text-gray-500"
                                    onClick={onManageTagsMenuClick}
                                >
                                    <Icon
                                        iconKey="general"
                                        size="0.625rem"
                                        className=" pe-1"
                                    />
                                    {i18next.t('tags.manage')}
                                </MenuItem>
                            </>
                        ) : null*/}
                    </Menu>
                </ClickAwayListener>
            ) : null}
            {/*manageTagsIsOpen && (
                <TicketManageTagsPopoverOverview
                    onClose={onCloseManageTagsPopover}
                    isOpen={manageTagsIsOpen}
                />
            )*/}
        </div>
    );
};

export default React.memo(TicketTagsList);
